import { createSdkFallbackArtwork } from "./brand-logo"
import { detectLoginEnvironment } from "./environment"
import {
	applyLocaleToUrl,
	getAutoResolvedLocale,
	type HostedFrameMessage,
	HostedFrameModal,
} from "./hosted-modal"

export type LoginEventType =
	| "LOGIN_READY"
	| "LOGIN_SUCCESS"
	| "LOGIN_CANCELLED"
	| "LOGIN_CLOSE"
	| "LOGIN_RESIZE"
	| "LOGIN_REDIRECT_REQUIRED"
	| "LOGIN_ERROR"

export type LoginDisplayMode = "auto" | "modal" | "popup" | "redirect"
export type LoginFallbackRedirectMode = "auto" | "manual"

export interface LoginResult {
	avatar?: string | null
	channel?: string
	email?: string | null
	expiresAt?: string
	legacyCasdoorId?: string
	loginToken?: string
	name?: string | null
	username?: string | null
	phoneCountryCode?: string | null
	phoneNumber?: string | null
	phoneE164?: string | null
	phoneVerifiedAt?: string | null
	userId?: string
	wechatOpenId?: string | null
	wechatUnionId?: string | null
}

export interface LoginEventData extends HostedFrameMessage, LoginResult {
	type: LoginEventType
	attemptId?: string
	authUrl?: string
	channel?: string
	error?: string
	message?: string
}

export interface LoginUIOptions {
	/** Origin to validate postMessage (defaults to '*', set for security) */
	allowedOrigin?: string
	/** Whether the SDK should close the login surface after receiving LOGIN_CLOSE. Defaults to true. */
	autoClose?: boolean
	/** Optional same-origin URL used as the no-API OAuth callback landing page. */
	callbackUrl?: string
	/**
	 * How to open the hosted login page. Defaults to "auto", which uses an
	 * iframe modal on desktop and redirects on mobile/embedded browsers.
	 */
	displayMode?: LoginDisplayMode
	/** Text for the fallback button shown when the iframe does not report readiness. */
	fallbackButtonText?: string
	/** Description for the fallback panel shown when the iframe does not report readiness. */
	fallbackDescription?: string
	/**
	 * Whether the SDK should redirect automatically when iframe loading times out.
	 * Defaults to "manual" to avoid taking over the integrator page when the
	 * hosted page is only slow to report readiness.
	 */
	fallbackRedirectMode?: LoginFallbackRedirectMode
	/** Title for the fallback panel shown when the iframe does not report readiness. */
	fallbackTitle?: string
	/**
	 * How long the SDK waits for LOGIN_READY or LOGIN_RESIZE before showing the
	 * fallback redirect button. Only used when displayMode is "modal".
	 * Defaults to 6000ms.
	 */
	iframeLoadTimeoutMs?: number
	/** Description shown while the hosted login iframe is loading. */
	loadingDescription?: string
	/** Title shown while the hosted login iframe is loading. */
	loadingTitle?: string
	/**
	 * Whether to poll popup.closed to detect manual popup closure.
	 * Only used when displayMode is "popup". Defaults to false because COOP can
	 * make popup.closed unreliable.
	 */
	monitorPopupClosed?: boolean
	onCancel?: () => void
	onClose?: () => void
	onError?: (message?: string, data?: LoginEventData) => void
	onFallbackVisible?: () => void
	onSuccess?: (result: LoginResult) => void
}

export interface LoginCallbackOptions {
	/**
	 * Whether the callback helper should close the current window after
	 * publishing the login result. Defaults to false; LoginUI closes the popup
	 * it opened after receiving the callback event.
	 */
	autoClose?: boolean
	onError?: (message?: string, data?: LoginEventData) => void
	onResult?: (data: LoginEventData) => void
	onSuccess?: (result: LoginResult) => void
}

export interface LoginParams {
	appId: string

	/**
	 * @deprecated Use loginUrl instead
	 * Defaults to https://pay.imgto.link
	 */
	baseUrl?: string

	/**
	 * Hosted login page base URL (e.g. https://pay.youidian.com)
	 * Defaults to https://pay.imgto.link
	 */
	loginUrl?: string

	/**
	 * Optional locale prefix to prepend to the hosted login path.
	 * If omitted, the SDK automatically resolves the locale from the browser language.
	 */
	locale?: string

	/** Preferred provider/channel code */
	preferredChannel?: string
}

function getOrigin(value?: string): string | null {
	if (!value) return null
	try {
		return new URL(value).origin
	} catch {
		return null
	}
}

function isValidLoginRedirectUrl(value?: string): value is string {
	if (!value) return false
	try {
		const url = new URL(value)
		return url.protocol === "https:" || url.protocol === "http:"
	} catch {
		return false
	}
}

function createLoginCallbackState() {
	if (typeof crypto !== "undefined" && crypto.randomUUID) {
		return crypto.randomUUID().replace(/-/g, "")
	}
	return `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`
}

function buildLoginCallbackUrl(callbackState: string, callbackUrl?: string) {
	const baseUrl = callbackUrl || window.location.href
	const url = new URL(baseUrl, window.location.href)
	const returnHash = url.hash.replace(/^#/, "")

	url.hash = ""
	url.searchParams.set(LOGIN_CALLBACK_MARKER, "1")
	url.searchParams.set(LOGIN_CALLBACK_STATE_PARAM, callbackState)
	if (returnHash) {
		url.searchParams.set(LOGIN_CALLBACK_RETURN_HASH_PARAM, returnHash)
	} else {
		url.searchParams.delete(LOGIN_CALLBACK_RETURN_HASH_PARAM)
	}

	return url.toString()
}

function buildLoginLaunchPayload(
	params: LoginParams,
	options?: LoginUIOptions,
	callbackState?: string,
) {
	const parentOrigin =
		typeof window !== "undefined" ? window.location.origin : undefined
	const callbackUrl =
		typeof window !== "undefined" && callbackState
			? buildLoginCallbackUrl(callbackState, options?.callbackUrl)
			: undefined

	return {
		autoClose: options?.autoClose ?? true,
		callbackUrl,
		callbackState,
		origin: parentOrigin,
		preferredChannel: params.preferredChannel,
	}
}

function appendLoginLaunchHash(
	urlValue: string,
	payload: ReturnType<typeof buildLoginLaunchPayload>,
) {
	const hashParams = new URLSearchParams()
	if (payload.autoClose === false) {
		hashParams.set("ydAutoClose", "false")
	}
	if (payload.origin) {
		hashParams.set("ydOrigin", payload.origin)
	}
	if (payload.callbackUrl) {
		hashParams.set("ydCallbackUrl", payload.callbackUrl)
	}
	if (payload.preferredChannel) {
		hashParams.set("ydPreferredChannel", payload.preferredChannel)
	}
	const hash = hashParams.toString()
	if (!hash) {
		return urlValue
	}

	try {
		const url = new URL(urlValue)
		url.hash = hash
		return url.toString()
	} catch (_error) {
		return `${urlValue}#${hash}`
	}
}

function buildLoginPopupName(payload: LoginLaunchPayload) {
	return `youidian-login:${JSON.stringify(payload)}`
}

function buildLoginUrl(
	params: LoginParams,
	options: LoginUIOptions | undefined,
	launchPayload: LoginLaunchPayload,
): string {
	const { appId, baseUrl = "https://pay.imgto.link", loginUrl } = params
	const rawBase = (loginUrl || baseUrl).replace(/\/$/, "")
	const parentOrigin = launchPayload.origin

	let finalUrl: string
	try {
		const url = new URL(rawBase)
		if (!/\/auth\/connect\/[^/]+$/.test(url.pathname)) {
			url.pathname =
				`${url.pathname.replace(/\/$/, "")}/auth/connect/${appId}`.replace(
					/\/{2,}/g,
					"/",
				)
		}
		finalUrl = url.toString()
	} catch (_error) {
		if (/\/auth\/connect\/[^/]+$/.test(rawBase)) {
			finalUrl = rawBase
		} else {
			finalUrl = `${rawBase}/auth/connect/${appId}`.replace(/\/{2,}/g, "/")
		}
	}

	finalUrl = applyLocaleToUrl(finalUrl, getAutoResolvedLocale(params.locale))

	try {
		const url = new URL(finalUrl)
		if (params.preferredChannel) {
			url.searchParams.set("preferredChannel", params.preferredChannel)
		}
		if (options?.autoClose === false) {
			url.searchParams.set("autoClose", "false")
		}
		if (parentOrigin) {
			url.searchParams.set("origin", parentOrigin)
		}
		return appendLoginLaunchHash(url.toString(), launchPayload)
	} catch (_error) {
		const searchParams = new URLSearchParams()
		if (params.preferredChannel) {
			searchParams.set("preferredChannel", params.preferredChannel)
		}
		if (options?.autoClose === false) {
			searchParams.set("autoClose", "false")
		}
		if (parentOrigin) {
			searchParams.set("origin", parentOrigin)
		}
		const query = searchParams.toString()
		if (!query) {
			return appendLoginLaunchHash(finalUrl, launchPayload)
		}
		const separator = finalUrl.includes("?") ? "&" : "?"
		return appendLoginLaunchHash(
			`${finalUrl}${separator}${query}`,
			launchPayload,
		)
	}
}

function extractLoginResult(data: LoginEventData): LoginResult {
	return {
		avatar: data.avatar,
		channel: data.channel,
		email: data.email,
		expiresAt: data.expiresAt,
		legacyCasdoorId: data.legacyCasdoorId,
		loginToken: data.loginToken,
		name: data.name,
		username: data.username,
		phoneCountryCode: data.phoneCountryCode,
		phoneNumber: data.phoneNumber,
		phoneE164: data.phoneE164,
		phoneVerifiedAt: data.phoneVerifiedAt,
		userId: data.userId,
		wechatOpenId: data.wechatOpenId,
		wechatUnionId: data.wechatUnionId,
	}
}

const LOGIN_UI_LOG_PREFIX = "[Youidian LoginUI]"
const LOGIN_CALLBACK_MARKER = "yd_login_callback"
const LOGIN_CALLBACK_RESULT_PARAM = "yd_login_result"
const LOGIN_CALLBACK_RETURN_HASH_PARAM = "yd_login_return_hash"
const LOGIN_CALLBACK_STATE_PARAM = "yd_login_state"
const LOGIN_CALLBACK_STORAGE_PREFIX = "yd-login-callback:"
const LOGIN_CALLBACK_CURRENT_PAGE_STORAGE_KEY = "yd-login-current-page-callback"
const DEFAULT_IFRAME_LOAD_TIMEOUT_MS = 6000

type LoginLaunchPayload = ReturnType<typeof buildLoginLaunchPayload>

function decodeCallbackPayload(value: string): LoginEventData | null {
	try {
		const padded = value.padEnd(
			value.length + ((4 - (value.length % 4)) % 4),
			"=",
		)
		const base64 = padded.replaceAll("-", "+").replaceAll("_", "/")
		const binary = atob(base64)
		const json = decodeURIComponent(
			Array.from(
				binary,
				(char) => `%${char.charCodeAt(0).toString(16).padStart(2, "0")}`,
			).join(""),
		)
		const parsed = JSON.parse(json) as LoginEventData
		return parsed && typeof parsed === "object" && parsed.type ? parsed : null
	} catch {
		return null
	}
}

function getLoginCallbackChannelName(state: string) {
	return `youidian-login:${state}`
}

function getLoginCallbackStorageKey(state: string) {
	return `${LOGIN_CALLBACK_STORAGE_PREFIX}${state}`
}

function publishLoginCallback(state: string, data: LoginEventData) {
	try {
		const channel = new BroadcastChannel(getLoginCallbackChannelName(state))
		channel.postMessage(data)
		channel.close()
	} catch {
		// BroadcastChannel is not available in every embedded browser.
	}

	try {
		const storageKey = getLoginCallbackStorageKey(state)
		window.localStorage.setItem(storageKey, JSON.stringify(data))
		window.setTimeout(() => {
			try {
				window.localStorage.removeItem(storageKey)
			} catch {
				// Ignore cleanup failures.
			}
		}, 800)
	} catch {
		// localStorage may be disabled; postMessage remains available below.
	}

	try {
		window.opener?.postMessage(data, window.location.origin)
	} catch {
		// opener is only an opportunistic fast path.
	}
}

function safeIsPopupClosed(popup: Window | null) {
	if (!popup) return true
	try {
		return popup.closed
	} catch {
		logLoginDebug("Unable to read popup.closed; treating popup as open")
		return false
	}
}

function safeClosePopup(popup: Window | null) {
	if (!popup) return
	try {
		popup.close()
	} catch {
		logLoginDebug("Unable to close popup")
	}
}

function cleanLoginCallbackUrl(url: URL) {
	url.searchParams.delete(LOGIN_CALLBACK_MARKER)
	url.searchParams.delete(LOGIN_CALLBACK_STATE_PARAM)
	const returnHash = url.searchParams.get(LOGIN_CALLBACK_RETURN_HASH_PARAM)
	url.searchParams.delete(LOGIN_CALLBACK_RETURN_HASH_PARAM)
	url.hash = returnHash ? `#${returnHash}` : ""
	return url.toString()
}

function saveCurrentPageLoginCallback(data: LoginEventData) {
	try {
		window.sessionStorage.setItem(
			LOGIN_CALLBACK_CURRENT_PAGE_STORAGE_KEY,
			JSON.stringify({
				createdAt: Date.now(),
				data,
			}),
		)
	} catch {
		// sessionStorage may be disabled in some embedded browsers.
	}
}

function readCurrentPageLoginCallback() {
	try {
		const raw = window.sessionStorage.getItem(
			LOGIN_CALLBACK_CURRENT_PAGE_STORAGE_KEY,
		)
		if (!raw) return null
		window.sessionStorage.removeItem(LOGIN_CALLBACK_CURRENT_PAGE_STORAGE_KEY)
		const parsed = JSON.parse(raw) as {
			createdAt?: number
			data?: LoginEventData
		} | null
		const data = parsed?.data
		return data && typeof data === "object" && data.type ? data : null
	} catch {
		return null
	}
}

function deliverLoginCallbackData(
	data: LoginEventData,
	options?: LoginCallbackOptions,
) {
	options?.onResult?.(data)
	switch (data.type) {
		case "LOGIN_SUCCESS":
			options?.onSuccess?.(extractLoginResult(data))
			break
		case "LOGIN_ERROR":
			options?.onError?.(data.message || data.error, data)
			break
		default:
			break
	}
}

export function handleLoginCallbackIfPresent(options?: LoginCallbackOptions) {
	if (typeof window === "undefined") {
		return false
	}

	const url = new URL(window.location.href)
	if (url.searchParams.get(LOGIN_CALLBACK_MARKER) !== "1") {
		if (options?.onResult || options?.onSuccess || options?.onError) {
			const pendingData = readCurrentPageLoginCallback()
			if (pendingData) {
				deliverLoginCallbackData(pendingData, options)
				return true
			}
		}
		return false
	}

	const state = url.searchParams.get(LOGIN_CALLBACK_STATE_PARAM) || ""
	const hashParams = new URLSearchParams(url.hash.replace(/^#/, ""))
	const rawResult = hashParams.get(LOGIN_CALLBACK_RESULT_PARAM)
	const data: LoginEventData | null =
		rawResult && state
			? decodeCallbackPayload(rawResult)
			: {
					type: "LOGIN_ERROR",
					message: "Missing login callback result",
				}

	try {
		document.documentElement.style.visibility = "hidden"
	} catch {
		// This is best-effort to avoid a visible callback-page flash.
	}

	if (state && data) {
		publishLoginCallback(state, data)
	}
	if (data && (options?.onResult || options?.onSuccess || options?.onError)) {
		deliverLoginCallbackData(data, options)
	} else if (data) {
		saveCurrentPageLoginCallback(data)
	}

	try {
		window.history.replaceState(null, "", cleanLoginCallbackUrl(url))
	} catch {
		// Ignore history cleanup failures.
	}

	if (options?.autoClose === true) {
		window.setTimeout(() => {
			try {
				window.close()
			} catch {
				// Some browsers disallow script-closing the current window.
			}
		}, 30)
		return true
	}

	try {
		document.documentElement.style.visibility = ""
	} catch {
		// Ignore visibility restoration failures.
	}

	return true
}

function logLoginDebug(message: string, details?: Record<string, unknown>) {
	if (typeof console === "undefined") return
	console.debug(LOGIN_UI_LOG_PREFIX, message, details || {})
}

function logLoginInfo(message: string, details?: Record<string, unknown>) {
	if (typeof console === "undefined") return
	console.info(LOGIN_UI_LOG_PREFIX, message, details || {})
}

function logLoginWarn(message: string, details?: Record<string, unknown>) {
	if (typeof console === "undefined") return
	console.warn(LOGIN_UI_LOG_PREFIX, message, details || {})
}

function getIframeLoadTimeoutMs(options?: LoginUIOptions) {
	const timeout = options?.iframeLoadTimeoutMs
	if (typeof timeout !== "number" || !Number.isFinite(timeout)) {
		return DEFAULT_IFRAME_LOAD_TIMEOUT_MS
	}
	return Math.max(0, timeout)
}

function applyLoginPanelStyle(panel: HTMLDivElement) {
	Object.assign(panel.style, {
		position: "absolute",
		inset: "0",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		border: "1px solid rgba(229,229,229,0.92)",
		borderRadius: "28px",
		background: "rgba(255,255,255,0.94)",
		color: "#0f172a",
		padding: "24px",
		textAlign: "center",
		zIndex: "1",
		boxShadow: "0 18px 50px rgba(15,15,15,0.06)",
		backdropFilter: "blur(16px)",
	})
}

function applyLoginLoadingPanelStyle(panel: HTMLDivElement) {
	Object.assign(panel.style, {
		position: "absolute",
		inset: "0",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		border: "0",
		borderRadius: "28px",
		background: "rgba(255,255,255,0.94)",
		color: "#0f172a",
		padding: "24px",
		textAlign: "center",
		zIndex: "1",
		boxShadow: "none",
		backdropFilter: "blur(16px)",
	})
}

function createLoginPanelContent() {
	const content = document.createElement("div")
	Object.assign(content.style, {
		maxWidth: "360px",
		width: "100%",
	})
	content.appendChild(
		createSdkFallbackArtwork({
			primary: "#171717",
			primary10: "rgba(23,23,23,0.1)",
			primary14: "rgba(23,23,23,0.14)",
			primary20: "rgba(23,23,23,0.2)",
			primary28: "rgba(23,23,23,0.28)",
			primary36: "rgba(23,23,23,0.36)",
			secondary: "#525252",
		}),
	)

	return content
}

function createBrandLoadingMark() {
	const logo = document.createElementNS("http://www.w3.org/2000/svg", "svg")
	logo.setAttribute("viewBox", "0 0 1024 1024")
	logo.setAttribute("fill", "none")
	logo.setAttribute("aria-hidden", "true")
	Object.assign(logo.style, {
		width: "112px",
		height: "112px",
		color: "#22c55e",
		display: "block",
		overflow: "visible",
	})

	const group = document.createElementNS("http://www.w3.org/2000/svg", "g")
	group.setAttribute("fill", "currentColor")

	const ring = document.createElementNS("http://www.w3.org/2000/svg", "path")
	ring.setAttribute(
		"d",
		"M704.298 679.54A264 264 0 1 1 704.298 309.46A49 49 0 0 1 634.4 378.149A166 166 0 1 0 634.4 610.851A49 49 0 0 1 704.298 679.54Z",
	)
	ring.setAttribute("class", "youidian-sdk-brand-loading__ring")

	const dot = document.createElementNS("http://www.w3.org/2000/svg", "circle")
	dot.setAttribute("class", "youidian-sdk-brand-loading__dot")
	dot.setAttribute("cx", "714.5")
	dot.setAttribute("cy", "490.5")
	dot.setAttribute("r", "68")

	group.appendChild(ring)
	group.appendChild(dot)
	logo.appendChild(group)

	return logo
}

type LoginLoadingBrandText = { name: string; tagline: string }

const DEFAULT_LOGIN_LOADING_BRAND_TEXT: LoginLoadingBrandText = {
	name: "Youidian",
	tagline: "Developer Service",
}

const LOGIN_LOADING_BRAND_TEXT: Record<string, LoginLoadingBrandText> = {
	de: { name: "Youidian", tagline: "Entwicklerservice" },
	en: DEFAULT_LOGIN_LOADING_BRAND_TEXT,
	es: { name: "Youidian", tagline: "Servicios para desarrolladores" },
	fr: { name: "Youidian", tagline: "Services pour développeurs" },
	it: { name: "Youidian", tagline: "Servizi per sviluppatori" },
	ja: { name: "Youidian", tagline: "開発者サービス" },
	ko: { name: "Youidian", tagline: "개발자 서비스" },
	nl: { name: "Youidian", tagline: "Ontwikkelaarsservice" },
	pt: { name: "Youidian", tagline: "Serviço para desenvolvedores" },
	"pt-BR": { name: "Youidian", tagline: "Serviço para desenvolvedores" },
	"zh-CN": { name: "优易点", tagline: "开发者服务中台" },
	"zh-Hant": { name: "優易點", tagline: "開發者服務平台" },
}

function getLoginLoadingBrandText(locale: string) {
	return LOGIN_LOADING_BRAND_TEXT[locale] || DEFAULT_LOGIN_LOADING_BRAND_TEXT
}

function createBrandLoadingCopy(locale: string) {
	const text = getLoginLoadingBrandText(locale)
	const brand = document.createElement("div")
	brand.className = "youidian-sdk-brand-loading__copy"
	Object.assign(brand.style, {
		display: "flex",
		flexDirection: "column",
		alignItems: "flex-start",
		justifyContent: "center",
		lineHeight: "1",
	})

	const name = document.createElement("div")
	name.className = "youidian-sdk-brand-loading__name"
	name.textContent = text.name
	Object.assign(name.style, {
		color: "#0f172a",
		fontSize: "34px",
		fontWeight: "900",
		letterSpacing: "0",
		lineHeight: "1",
	})

	const tagline = document.createElement("div")
	tagline.className = "youidian-sdk-brand-loading__tagline"
	tagline.textContent = text.tagline
	Object.assign(tagline.style, {
		color: "#64748b",
		fontSize: "16px",
		fontWeight: "700",
		letterSpacing: "0.24em",
		lineHeight: "1",
		marginTop: "6px",
	})

	brand.appendChild(name)
	brand.appendChild(tagline)
	return brand
}

function createLoginPanelTitle(value: string) {
	const title = document.createElement("div")
	title.textContent = value
	Object.assign(title.style, {
		color: "#171717",
		fontSize: "18px",
		fontWeight: "700",
		lineHeight: "1.35",
		marginBottom: "10px",
	})
	return title
}

function createLoginPanelDescription(value: string) {
	const description = document.createElement("div")
	description.textContent = value
	Object.assign(description.style, {
		color: "#525252",
		fontSize: "14px",
		lineHeight: "1.6",
		marginBottom: "20px",
	})
	return description
}

function ensureLoginLoadingStyles() {
	if (document.getElementById("youidian-login-loading-style")) {
		return
	}
	const style = document.createElement("style")
	style.id = "youidian-login-loading-style"
	style.textContent = `
		.youidian-sdk-brand-loading__ring,
		.youidian-sdk-brand-loading__dot {
			transform-box: fill-box;
			transform-origin: center;
			will-change: opacity, transform;
		}

		.youidian-sdk-brand-loading__ring {
			animation: youidian-sdk-brand-loading-ring 1400ms cubic-bezier(0.4, 0, 0.2, 1) infinite;
		}

		.youidian-sdk-brand-loading__dot {
			animation: youidian-sdk-brand-loading-dot 1400ms cubic-bezier(0.4, 0, 0.2, 1) infinite;
		}

		.youidian-sdk-brand-loading__copy {
			will-change: opacity, transform;
			animation: youidian-sdk-brand-loading-copy 1600ms cubic-bezier(0.4, 0, 0.2, 1) infinite;
		}

		.youidian-sdk-brand-loading__tagline {
			will-change: opacity;
			animation: youidian-sdk-brand-loading-tagline 1600ms cubic-bezier(0.4, 0, 0.2, 1) infinite;
		}

		@keyframes youidian-sdk-brand-loading-ring {
			0%,
			100% {
				opacity: 0.72;
				transform: rotate(0deg) scale(0.98);
			}
			50% {
				opacity: 1;
				transform: rotate(8deg) scale(1.02);
			}
		}

		@keyframes youidian-sdk-brand-loading-dot {
			0%,
			100% {
				opacity: 0.55;
				transform: scale(0.9);
			}
			50% {
				opacity: 1;
				transform: scale(1.08);
			}
		}

		@keyframes youidian-sdk-brand-loading-copy {
			0%,
			100% {
				opacity: 0.86;
				transform: translateY(0);
			}
			50% {
				opacity: 1;
				transform: translateY(-1px);
			}
		}

		@keyframes youidian-sdk-brand-loading-tagline {
			0%,
			100% {
				opacity: 0.64;
			}
			50% {
				opacity: 0.92;
			}
		}

		@media (prefers-reduced-motion: reduce) {
			.youidian-sdk-brand-loading__ring,
			.youidian-sdk-brand-loading__dot,
			.youidian-sdk-brand-loading__copy,
			.youidian-sdk-brand-loading__tagline {
				animation: none;
				opacity: 1;
				transform: none;
			}
		}
	`
	document.head.appendChild(style)
}

function createLoginLoadingPanel(locale: string) {
	ensureLoginLoadingStyles()
	const text = getLoginLoadingBrandText(locale)
	const panel = document.createElement("div")
	applyLoginLoadingPanelStyle(panel)
	panel.setAttribute("role", "status")
	panel.setAttribute("aria-label", `${text.name} ${text.tagline}`)

	const content = document.createElement("div")
	Object.assign(content.style, {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		gap: "12px",
	})
	content.appendChild(createBrandLoadingMark())
	content.appendChild(createBrandLoadingCopy(locale))
	panel.appendChild(content)

	return panel
}

function createLoginRedirectingPanel(_options: {
	description: string
	locale: string
	title: string
}) {
	return createLoginLoadingPanel(_options.locale)
}

function createLoginFallbackPanel(options: {
	buttonText: string
	description: string
	onRedirect: () => void
	title: string
}) {
	const panel = document.createElement("div")
	applyLoginPanelStyle(panel)

	const content = createLoginPanelContent()

	const button = document.createElement("button")
	button.type = "button"
	button.textContent = options.buttonText
	Object.assign(button.style, {
		width: "100%",
		border: "0",
		borderRadius: "12px",
		background: "#0a0a0a",
		color: "#ffffff",
		cursor: "pointer",
		fontSize: "14px",
		fontWeight: "700",
		minHeight: "44px",
		padding: "12px 16px",
		boxShadow: "0 12px 28px rgba(17,17,17,0.14)",
	})
	button.onclick = options.onRedirect

	content.appendChild(createLoginPanelTitle(options.title))
	content.appendChild(createLoginPanelDescription(options.description))
	content.appendChild(button)
	panel.appendChild(content)

	return panel
}

export class LoginUI extends HostedFrameModal<LoginEventData> {
	private popup: Window | null = null
	private callbackChannel: BroadcastChannel | null = null
	private callbackStorageHandler: ((event: StorageEvent) => void) | null = null
	private popupMessageHandler: ((event: MessageEvent) => void) | null = null
	private closeMonitor: number | null = null
	private iframeFallbackPanel: HTMLDivElement | null = null
	private iframeLoadingPanel: HTMLDivElement | null = null
	private iframeLoadTimer: number | null = null
	private iframeRedirectTimer: number | null = null
	private iframeReady = false
	private completed = false

	private cleanup() {
		if (this.callbackChannel) {
			this.callbackChannel.close()
		}
		if (typeof window !== "undefined" && this.callbackStorageHandler) {
			window.removeEventListener("storage", this.callbackStorageHandler)
		}
		if (typeof window !== "undefined" && this.popupMessageHandler) {
			window.removeEventListener("message", this.popupMessageHandler)
		}
		this.callbackChannel = null
		this.callbackStorageHandler = null
		if (typeof window !== "undefined" && this.closeMonitor) {
			window.clearInterval(this.closeMonitor)
		}
		if (typeof window !== "undefined" && this.iframeLoadTimer) {
			window.clearTimeout(this.iframeLoadTimer)
		}
		if (typeof window !== "undefined" && this.iframeRedirectTimer) {
			window.clearTimeout(this.iframeRedirectTimer)
		}
		if (this.iframeFallbackPanel?.parentNode) {
			this.iframeFallbackPanel.parentNode.removeChild(this.iframeFallbackPanel)
		}
		if (this.iframeLoadingPanel?.parentNode) {
			this.iframeLoadingPanel.parentNode.removeChild(this.iframeLoadingPanel)
		}
		this.popupMessageHandler = null
		this.closeMonitor = null
		this.iframeFallbackPanel = null
		this.iframeLoadingPanel = null
		this.iframeLoadTimer = null
		this.iframeRedirectTimer = null
		this.iframeReady = false
		this.popup = null
		this.completed = false
	}

	private markIframeReady() {
		if (this.iframeReady) return
		this.iframeReady = true
		if (typeof window !== "undefined" && this.iframeLoadTimer) {
			window.clearTimeout(this.iframeLoadTimer)
			this.iframeLoadTimer = null
		}
		if (typeof window !== "undefined" && this.iframeRedirectTimer) {
			window.clearTimeout(this.iframeRedirectTimer)
			this.iframeRedirectTimer = null
		}
		if (this.iframeFallbackPanel?.parentNode) {
			this.iframeFallbackPanel.parentNode.removeChild(this.iframeFallbackPanel)
		}
		if (this.iframeLoadingPanel?.parentNode) {
			this.iframeLoadingPanel.parentNode.removeChild(this.iframeLoadingPanel)
		}
		this.iframeFallbackPanel = null
		this.iframeLoadingPanel = null
	}

	private showIframeLoading(container: HTMLDivElement, locale: string) {
		if (this.iframeLoadingPanel || this.iframeReady) {
			return
		}

		const panel = createLoginLoadingPanel(locale)
		this.iframeLoadingPanel = panel
		container.appendChild(panel)
	}

	private showIframeFallback(options: {
		container: HTMLDivElement
		finalUrl: string
		launchPayload: LoginLaunchPayload
		loginOptions?: LoginUIOptions
		locale: string
	}) {
		if (this.iframeReady || this.iframeFallbackPanel) {
			return
		}

		const { container, finalUrl, loginOptions } = options
		logLoginWarn("Hosted login iframe did not report ready in time", {
			loginUrl: finalUrl,
		})
		if (this.iframeLoadingPanel?.parentNode) {
			this.iframeLoadingPanel.parentNode.removeChild(this.iframeLoadingPanel)
		}
		this.iframeLoadingPanel = null

		if (loginOptions?.fallbackRedirectMode === "auto") {
			const panel = createLoginRedirectingPanel({
				description:
					loginOptions?.fallbackDescription ||
					"The embedded login page is taking longer than expected. Redirecting you to the login page now.",
				locale: options.locale,
				title: loginOptions?.fallbackTitle || "Redirecting to login page",
			})
			this.iframeFallbackPanel = panel
			container.appendChild(panel)
			loginOptions?.onFallbackVisible?.()
			this.iframeRedirectTimer = window.setTimeout(() => {
				this.iframeRedirectTimer = null
				logLoginInfo("Redirecting to hosted login fallback page", {
					loginUrl: finalUrl,
				})
				window.location.assign(finalUrl)
			}, 350)
			return
		}

		const panel = createLoginFallbackPanel({
			buttonText: loginOptions?.fallbackButtonText || "Continue to login",
			description:
				loginOptions?.fallbackDescription ||
				"The embedded login page is taking longer than expected. Continue on the login page to finish signing in.",
			title: loginOptions?.fallbackTitle || "Login page is still loading",
			onRedirect: () => {
				logLoginInfo("Redirecting to hosted login fallback page", {
					loginUrl: finalUrl,
				})
				window.location.assign(finalUrl)
			},
		})

		this.iframeFallbackPanel = panel
		container.appendChild(panel)
		loginOptions?.onFallbackVisible?.()
	}

	private startIframeWatchdog(options: {
		container: HTMLDivElement
		finalUrl: string
		launchPayload: LoginLaunchPayload
		loginOptions?: LoginUIOptions
		locale: string
	}) {
		if (typeof window === "undefined") return

		const timeoutMs = getIframeLoadTimeoutMs(options.loginOptions)
		if (timeoutMs === 0) {
			this.showIframeFallback(options)
			return
		}

		this.iframeLoadTimer = window.setTimeout(() => {
			this.iframeLoadTimer = null
			this.showIframeFallback(options)
		}, timeoutMs)
	}

	private handleLoginEvent(data: LoginEventData, options?: LoginUIOptions) {
		if (!data || typeof data !== "object" || !data.type) {
			logLoginDebug("Ignored non-login event payload")
			return
		}

		logLoginInfo("Received login event", {
			type: data.type,
		})

		switch (data.type) {
			case "LOGIN_READY":
				break
			case "LOGIN_SUCCESS":
				if (this.completed) {
					break
				}
				this.completed = true
				logLoginInfo("Login succeeded", {
					channel: data.channel || null,
					userId: data.userId || null,
				})
				options?.onSuccess?.(extractLoginResult(data))
				break
			case "LOGIN_CANCELLED":
				logLoginInfo("Login cancelled")
				options?.onCancel?.()
				break
			case "LOGIN_RESIZE":
				break
			case "LOGIN_REDIRECT_REQUIRED":
				if (!isValidLoginRedirectUrl(data.authUrl)) {
					logLoginWarn("Login redirect requested with invalid authUrl")
					options?.onError?.("Login redirect URL is invalid", data)
					break
				}
				logLoginInfo("Login redirect requested by hosted page", {
					attemptId: data.attemptId || null,
					channel: data.channel || null,
					authUrl: data.authUrl,
				})
				this.close()
				window.location.assign(data.authUrl)
				break
			case "LOGIN_ERROR":
				if (this.completed) {
					break
				}
				logLoginWarn("Login flow reported an error", {
					message: data.message || data.error || null,
				})
				options?.onError?.(data.message || data.error, data)
				break
			case "LOGIN_CLOSE":
				if (options?.autoClose === false) {
					logLoginInfo(
						"Login surface requested close; autoClose disabled, keeping it open",
					)
					options?.onClose?.()
					break
				}
				logLoginInfo("Login surface requested close; autoClose enabled")
				this.close()
				options?.onClose?.()
				break
		}
	}

	private openLoginRedirect(params: LoginParams, options?: LoginUIOptions) {
		if (typeof window === "undefined") return

		const { callbackState, finalUrl, launchPayload } = this.buildOpenContext(
			params,
			options,
		)
		logLoginInfo("Redirecting to hosted login page", {
			appId: params.appId,
			callbackState,
			callbackUrl: launchPayload.callbackUrl || null,
			hasCallbackUrl: Boolean(launchPayload.callbackUrl),
			loginUrl: finalUrl,
			autoClose: options?.autoClose ?? true,
			displayMode: "redirect",
			pageUrl: window.location.href,
			parentOrigin: launchPayload.origin || null,
		})
		window.location.assign(finalUrl)
	}

	private listenForLoginCallback(
		callbackState: string,
		options?: LoginUIOptions,
	) {
		try {
			this.callbackChannel = new BroadcastChannel(
				getLoginCallbackChannelName(callbackState),
			)
			this.callbackChannel.onmessage = (event) => {
				this.handleLoginEvent(event.data as LoginEventData, options)
				this.handleLoginEvent({ type: "LOGIN_CLOSE" }, options)
			}
		} catch {
			logLoginDebug("BroadcastChannel is unavailable for login callback")
		}

		this.callbackStorageHandler = (event: StorageEvent) => {
			if (
				event.key !== getLoginCallbackStorageKey(callbackState) ||
				!event.newValue
			) {
				return
			}
			try {
				const data = JSON.parse(event.newValue) as LoginEventData
				this.handleLoginEvent(data, options)
				this.handleLoginEvent({ type: "LOGIN_CLOSE" }, options)
			} catch {
				logLoginWarn("Failed to parse login callback storage payload")
			}
		}
		window.addEventListener("storage", this.callbackStorageHandler)
	}

	close() {
		logLoginInfo("close() called", {
			hasModal: Boolean(this.modal),
			hasPopup: Boolean(this.popup),
			popupClosed: safeIsPopupClosed(this.popup),
		})
		safeClosePopup(this.popup)
		super.close()
		this.cleanup()
	}

	private buildOpenContext(params: LoginParams, options?: LoginUIOptions) {
		const callbackState = createLoginCallbackState()
		const launchPayload = buildLoginLaunchPayload(
			params,
			options,
			callbackState,
		)
		const finalUrl = buildLoginUrl(params, options, launchPayload)
		return { callbackState, finalUrl, launchPayload }
	}

	private openLoginModal(params: LoginParams, options?: LoginUIOptions) {
		if (typeof document === "undefined") return
		if (this.modal) return

		const { callbackState, finalUrl, launchPayload } = this.buildOpenContext(
			params,
			options,
		)
		const loginOrigin = getOrigin(finalUrl)
		logLoginInfo("Opening hosted login iframe modal", {
			appId: params.appId,
			callbackState,
			callbackUrl: launchPayload.callbackUrl || null,
			hasCallbackUrl: Boolean(launchPayload.callbackUrl),
			loginUrl: finalUrl,
			allowedOrigin: options?.allowedOrigin || null,
			autoClose: options?.autoClose ?? true,
			displayMode: "modal",
			pageUrl: typeof window !== "undefined" ? window.location.href : null,
			parentOrigin: launchPayload.origin || null,
		})

		this.completed = false
		this.iframeReady = false
		this.listenForLoginCallback(callbackState, options)
		const hostedFrame = this.openHostedFrame(finalUrl, {
			allowedOrigin: options?.allowedOrigin || loginOrigin || undefined,
			height: "min(760px, 94vh)",
			onCloseButton: () => {
				options?.onCancel?.()
				options?.onClose?.()
			},
			onMessage: (data, _container, event) => {
				const isIframeEvent = event.source === this.iframe?.contentWindow
				// 诊断：LOGIN_READY/LOGIN_RESIZE 到达但 event.source 不匹配当前 iframe
				// 时，markIframeReady 不会执行，loading 面板会卡住直至 6s fallback。
				// 这类「iframe 不出现」曾无任何日志，加 warn 便于线上定位根因
				// （常见诱因：iframe 加载了被缓存/重定向的旧文档导致 contentWindow
				// 身份变化，或同一页面挂了多个 SDK 实例）。
				if (
					!isIframeEvent &&
					(data.type === "LOGIN_READY" || data.type === "LOGIN_RESIZE")
				) {
					logLoginWarn(
						"Login lifecycle event ignored: event.source does not match iframe contentWindow",
						{
							type: data.type,
							eventOrigin: event.origin,
							hasIframe: Boolean(this.iframe),
							hasIframeContentWindow: Boolean(this.iframe?.contentWindow),
						},
					)
				}
				if (
					(isIframeEvent &&
						(data.type === "LOGIN_READY" || data.type === "LOGIN_RESIZE")) ||
					data.type === "LOGIN_SUCCESS" ||
					data.type === "LOGIN_ERROR"
				) {
					this.markIframeReady()
				}
				this.handleLoginEvent(data, options)
			},
			width: "min(520px, 100%)",
		})

		if (hostedFrame) {
			this.showIframeLoading(
				hostedFrame.container,
				getAutoResolvedLocale(params.locale),
			)
			this.startIframeWatchdog({
				container: hostedFrame.container,
				finalUrl,
				launchPayload,
				loginOptions: options,
				locale: getAutoResolvedLocale(params.locale),
			})
		}
	}

	private openLoginPopup(params: LoginParams, options?: LoginUIOptions) {
		if (typeof window === "undefined") return
		if (this.popup && !safeIsPopupClosed(this.popup)) return

		const { callbackState, finalUrl, launchPayload } = this.buildOpenContext(
			params,
			options,
		)
		logLoginInfo("Opening hosted login popup", {
			appId: params.appId,
			callbackState,
			callbackUrl: launchPayload.callbackUrl || null,
			hasCallbackUrl: Boolean(launchPayload.callbackUrl),
			loginUrl: finalUrl,
			allowedOrigin: options?.allowedOrigin || null,
			autoClose: options?.autoClose ?? true,
			displayMode: "popup",
			pageUrl: typeof window !== "undefined" ? window.location.href : null,
			parentOrigin: launchPayload.origin || null,
		})
		const popupWidth = 520
		const popupHeight = 720
		const left = Math.max(
			0,
			Math.round(window.screenX + (window.outerWidth - popupWidth) / 2),
		)
		const top = Math.max(
			0,
			Math.round(window.screenY + (window.outerHeight - popupHeight) / 2),
		)
		const features = [
			"popup=yes",
			`width=${popupWidth}`,
			`height=${popupHeight}`,
			`left=${left}`,
			`top=${top}`,
			"resizable=yes",
			"scrollbars=yes",
		].join(",")

		const popupName = buildLoginPopupName(launchPayload)
		const popup = window.open(finalUrl, popupName, features)
		if (!popup) {
			logLoginWarn("Popup blocked by the browser")
			options?.onError?.("Login popup was blocked")
			return
		}

		this.popup = popup
		this.completed = false
		this.listenForLoginCallback(callbackState, options)

		const allowedOrigin = options?.allowedOrigin
		const popupOrigin = getOrigin(finalUrl)

		this.popupMessageHandler = (event: MessageEvent) => {
			if (
				allowedOrigin &&
				allowedOrigin !== "*" &&
				event.origin !== allowedOrigin
			) {
				logLoginWarn("Ignored message due to allowedOrigin mismatch", {
					allowedOrigin,
					eventOrigin: event.origin,
				})
				return
			}
			if (!allowedOrigin && popupOrigin && event.origin !== popupOrigin) {
				logLoginWarn("Ignored message due to popup origin mismatch", {
					expectedOrigin: popupOrigin,
					eventOrigin: event.origin,
				})
				return
			}

			const data = event.data as LoginEventData
			if (!data || typeof data !== "object" || !data.type) {
				logLoginDebug("Ignored non-login postMessage payload")
				return
			}

			logLoginInfo("Received login event", {
				type: data.type,
				eventOrigin: event.origin,
			})

			this.handleLoginEvent(data, options)
		}

		window.addEventListener("message", this.popupMessageHandler)

		if (options?.monitorPopupClosed === true) {
			this.closeMonitor = window.setInterval(() => {
				if (!this.popup || safeIsPopupClosed(this.popup)) {
					const shouldTreatAsCancel = !this.completed
					logLoginInfo("Detected popup closed", {
						shouldTreatAsCancel,
					})
					this.cleanup()
					if (shouldTreatAsCancel) {
						options?.onCancel?.()
					}
					options?.onClose?.()
				}
			}, 500)
		}
	}

	openLogin(params: LoginParams, options?: LoginUIOptions) {
		if (typeof window === "undefined") return

		const displayMode = options?.displayMode ?? "auto"
		if (displayMode === "redirect") {
			this.openLoginRedirect(params, options)
			return
		}
		if (displayMode === "popup") {
			this.openLoginPopup(params, options)
			return
		}
		if (
			displayMode === "auto" &&
			detectLoginEnvironment().shouldUseRedirectLogin
		) {
			this.openLoginRedirect(params, options)
			return
		}
		this.openLoginModal(params, options)
	}
}

export function createLoginUI(): LoginUI {
	return new LoginUI()
}

handleLoginCallbackIfPresent({ autoClose: false })
